/**
 * Shared floating popover primitive for action menus and editing surfaces.
 *
 * Supports item-list menus, custom rendered content, owner-scoped lifecycles,
 * element or caret anchors, viewport clamping, focus trapping, and bridge
 * event dismissals.
 */

import { eventBus } from '../core/events.js';
import { isComposing, isTextEntryControl } from '../shared/typing-target.js';
import {
  getDockedRightEdge,
  getSafeAreaInsets,
  isPhoneViewport,
  onViewportChange
} from '../shared/viewport-service.js';
import { trapFocus } from './focus-trap.js';
import { iconEl } from './icons.js';

const _activeByOwner = new Map();

/** True only for an active portaled menu anchored inside this owning surface. */
export function isOwnedPopoverTarget(target, ownerEl) {
  if (!(target instanceof Node) || !ownerEl) return false;
  let node = target;
  const visited = new Set();
  while (node && !visited.has(node)) {
    visited.add(node);
    const record = [..._activeByOwner.values()].find((entry) => entry.menu.contains(node));
    const anchor = record?.triggerEl;
    if (!record?.menu.isConnected || !anchor?.isConnected) return false;
    if (ownerEl.contains(anchor)) return true;
    node = anchor;
  }
  return false;
}

/**
 * The exit duration a caller opts into with `exitAnimation` (contracts §9).
 *
 * Exported rather than hardcoded at each call site so the CSS transition and the
 * removal timer cannot drift — a timer shorter than the transition removes the
 * menu mid-fade, and a longer one leaves an invisible node absorbing nothing but
 * still in the DOM.
 */
export const MENU_EXIT_MS = 100;

function _prefersReducedMotion() {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    // Expected flow: jsdom has no matchMedia. No preference means no animation
    // suppression, which is the same answer as "the user has not asked for it".
    return false;
  }
}

function _noopHandle() {
  const handle = function close() {};
  handle.reposition = () => {};
  return handle;
}

function _resolveAnchorRect(triggerEl, opts) {
  if (typeof opts?.getAnchorRect === 'function') return opts.getAnchorRect();
  if (opts?.anchorRect) return opts.anchorRect;
  if (triggerEl?.getBoundingClientRect) return triggerEl.getBoundingClientRect();
  return null;
}

function _isCaretAnchored(opts) {
  return typeof opts?.getAnchorRect === 'function' || !!opts?.anchorRect;
}

function _num(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function _visualFrame() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const left = _num(vv?.offsetLeft, 0);
  const top = _num(vv?.offsetTop, 0);
  const width = _num(vv?.width, typeof window !== 'undefined' ? window.innerWidth : 0);
  const height = _num(vv?.height, typeof window !== 'undefined' ? window.innerHeight : 0);
  // A docked panel floats over the right of the viewport, so the usable frame
  // ends where the panel begins. This is now PLACEMENT hygiene rather than a
  // correctness requirement — the portal sits at --pb-z-popover-portal, above
  // the docked tier, so an unavoidable overlap paints the right way round —
  // but a menu that can sit beside a panel still should.
  const dockedEdge = getDockedRightEdge();
  const right = Number.isFinite(dockedEdge)
    ? Math.max(left, Math.min(left + width, dockedEdge))
    : left + width;
  return {
    left,
    top,
    width: right - left,
    height,
    right,
    bottom: top + height
  };
}

function _safeGutters() {
  const safe = getSafeAreaInsets();
  return {
    top: Math.max(8, _num(safe.top)),
    right: Math.max(8, _num(safe.right)),
    bottom: Math.max(8, _num(safe.bottom)),
    left: Math.max(8, _num(safe.left))
  };
}

function _layoutBottomOffset(frame, gutters) {
  const layoutHeight =
    typeof window !== 'undefined' ? _num(window.innerHeight, frame.height) : frame.height;
  return Math.max(0, layoutHeight - frame.bottom) + gutters.bottom;
}

function _wantsPhoneSheet(opts) {
  if (!isPhoneViewport()) return false;
  if (opts?.phoneSheet === true) return true;
  return opts?.phoneSheet === 'auto' && !_isCaretAnchored(opts);
}

function _isDisabledControl(el) {
  return !!(el?.disabled || el?.getAttribute?.('aria-disabled') === 'true');
}

function _getFocusableItems(menu) {
  return Array.from(
    menu.querySelectorAll('button, [tabindex="0"], input, select, textarea')
  ).filter((el) => !_isDisabledControl(el));
}

function _closeOwner(owner) {
  const active = _activeByOwner.get(owner);
  if (active) active.close();
}

function _setAriaExpanded(triggerEl, value) {
  if (triggerEl?.setAttribute) triggerEl.setAttribute('aria-expanded', value);
}

function _consumeDismissShieldEvent(event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
}

function _removeDismissShield(shield) {
  if (shield?.parentNode) shield.parentNode.removeChild(shield);
}

function _deferDismissShieldRemoval(shield) {
  if (!shield) return;
  const consumeAndRemove = (event) => {
    _consumeDismissShieldEvent(event);
    _removeDismissShield(shield);
  };
  shield.addEventListener('pointerup', consumeAndRemove, { once: true });
  shield.addEventListener('mouseup', consumeAndRemove, { once: true });
  shield.addEventListener('click', consumeAndRemove, { once: true });
  shield.addEventListener('touchend', consumeAndRemove, { once: true });
  setTimeout(() => _removeDismissShield(shield), 240);
}

function _createDismissShield(close, deferRemoval) {
  const wrap = document.querySelector('.pb-iframe-wrap');
  if (!wrap) return null;

  const shield = document.createElement('div');
  shield.className = 'pb-popover-dismiss-shield';
  shield.setAttribute('aria-hidden', 'true');
  shield.addEventListener('pointerdown', (event) => {
    _consumeDismissShieldEvent(event);
    deferRemoval();
    close();
  });
  wrap.appendChild(shield);
  return shield;
}

/**
 * @typedef {Object} PopoverMenuItem
 * @property {string} [id]
 * @property {string} label
 * @property {() => void} [onSelect]
 * @property {boolean} [danger]
 * @property {boolean} [disabled]
 * @property {string} [disabledReason]
 * @property {boolean} [showReason] — BC-9: render `disabledReason` as a visible
 *   line under the label. Opt-in; a touch user never sees a `title` tooltip.
 * @property {string} [shortcut]
 * @property {string} [icon] — trusted SVG markup rendered before the label
 */

/**
 * @typedef {Object} PopoverMenuSeparator
 * @property {'separator'} type
 * @property {boolean} [danger]
 */

/**
 * Open a floating popover.
 *
 * @param {HTMLElement|null} triggerEl
 * @param {{
 *   items?: Array<PopoverMenuItem | PopoverMenuSeparator>,
 *   render?: ({ close, menu, anchorRect }: { close: () => void, menu: HTMLElement, anchorRect: DOMRect|Object }) => HTMLElement|DocumentFragment|null,
 *   owner?: string,
 *   align?: 'right' | 'left' | 'center',
 *   gap?: number,
 *   role?: string,
 *   menuLabel?: string,
 *   width?: number,
 *   className?: string,
 *   useBaseClass?: boolean,
 *   anchorRect?: DOMRect|Object,
 *   getAnchorRect?: () => DOMRect|Object|null,
 *   disableArrowNav?: boolean,
 *   dismiss?: { outsideClick?: boolean, escape?: boolean, iframeClick?: boolean, panelTabChange?: boolean, sidebarClose?: boolean },
 *   outsideClickFilter?: (event: Event) => boolean,
 *   consumeCanvasDismiss?: boolean,
 *   phoneSheet?: false | 'auto' | true,
 *   initialFocus?: false|string|Element|(() => false|string|Element|null),
 *   exitAnimation?: number,
 *   restoreFocus?: ({ menu, triggerEl }: { menu: HTMLElement, triggerEl: HTMLElement|null }) => void,
 *   onClose?: () => void
 * }} opts
 * @returns {Function & { reposition: () => void }} close handle
 */
export function openPopoverMenu(triggerEl, opts = {}) {
  const hasRender = typeof opts.render === 'function';
  const hasItems = Array.isArray(opts.items) && opts.items.length > 0;
  if (!hasRender && !hasItems) return _noopHandle();

  const initialAnchorRect = _resolveAnchorRect(triggerEl, opts);
  if (!initialAnchorRect) return _noopHandle();

  const owner = opts.owner || 'default';

  // Re-invoking from the SAME trigger TOGGLES, and this belongs here rather
  // than in each caller. `onDocClick` deliberately ignores a mousedown on the
  // trigger — otherwise the click that opens a menu would immediately close it
  // again — so nothing closed a trigger-anchored menu when its own control was
  // clicked. Callers that keep the handle (ToolbarMenu, account-panel) toggle
  // before reaching this and are unaffected; the ones that DISCARD it fell
  // straight through to a fresh open, which is why Saved, All settings and Help
  // appeared to reopen on the click meant to dismiss them.
  if (triggerEl) {
    const active = _activeByOwner.get(owner);
    if (active && active.triggerEl === triggerEl) {
      active.close();
      return _noopHandle();
    }
  }
  _closeOwner(owner);

  const align = opts.align === 'left' || opts.align === 'center' ? opts.align : 'right';
  const width = Number.isFinite(opts.width) ? opts.width : 220;
  const dismissOpts = {
    outsideClick: true,
    escape: true,
    iframeClick: true,
    panelTabChange: true,
    sidebarClose: true,
    ...(opts.dismiss || {})
  };

  const menu = document.createElement('div');
  const baseClass = opts.useBaseClass === false ? '' : 'pb-popover-menu';
  menu.className = [baseClass, opts.className || ''].filter(Boolean).join(' ') || 'pb-popover-menu';
  menu.dataset.pbKit = 'popover-menu';
  menu.setAttribute('role', opts.role || 'menu');
  if (opts.menuLabel) menu.setAttribute('aria-label', opts.menuLabel);
  menu.style.position = 'fixed';
  if (Number.isFinite(opts.width) || opts.useBaseClass !== false) {
    menu.style.minWidth = `${width}px`;
  }
  menu.style.zIndex = 'var(--pb-z-popover-portal)';

  let record = null;
  let closed = false;
  let focusableItems = [];
  let dismissShield = null;
  let deferShieldRemoval = false;
  let viewportUnsubscribe = null;

  function close() {
    if (closed) return;
    closed = true;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(docListenerFrame);
    if (docListenersInstalled) {
      document.removeEventListener('mousedown', onDocClick, true);
      document.removeEventListener('touchstart', onDocClick, true);
    }
    window.removeEventListener('resize', onWinChange);
    window.removeEventListener('scroll', onWinChange, true);
    viewportUnsubscribe?.();
    viewportUnsubscribe = null;
    for (const cleanup of eventCleanups.splice(0)) {
      try {
        cleanup();
      } catch (err) {
        console.error('[popover-menu] cleanup threw:', err);
      }
    }
    menu.removeEventListener('trap-escape', onTrapEscape);
    menu.removeEventListener('keydown', onKey);
    releaseTrap();
    if (dismissShield) {
      if (deferShieldRemoval) {
        _deferDismissShieldRemoval(dismissShield);
      } else {
        _removeDismissShield(dismissShield);
      }
      dismissShield = null;
    }
    _setAriaExpanded(triggerEl, 'false');
    menu.classList.remove('pb-popover-menu-open');
    _detachMenu();
    if (_activeByOwner.get(owner) === record) _activeByOwner.delete(owner);
    if (typeof opts.restoreFocus === 'function') {
      try {
        opts.restoreFocus({ menu, triggerEl });
      } catch (err) {
        console.error('[popover-menu] restoreFocus threw:', err);
      }
    } else if (typeof triggerEl?.focus === 'function' && document.body.contains(triggerEl)) {
      triggerEl.focus();
    }
    if (typeof opts.onClose === 'function') {
      try {
        opts.onClose();
      } catch (err) {
        console.error('[popover-menu] onClose threw:', err);
      }
    }
  }

  /**
   * Remove the menu — after a fade, but ONLY if the caller opted in.
   *
   * The primitive removed the node synchronously in `close()` for its whole
   * existence and a dozen surfaces depend on that (a reopen in the same tick, a
   * test asserting the node is gone, an `owner` handoff). So the animation is
   * opt-in per call, and even then everything else about closing stays
   * synchronous: focus is already restored, `aria-expanded` is already false,
   * the owner slot is released on the next line, and the fading node is made
   * inert and hidden from assistive tech so it cannot absorb a click or be
   * announced. What lingers is pixels.
   *
   * Under `prefers-reduced-motion` there is no lingering at all — contracts §9
   * says `transition: none`, and a deferred removal with no transition is a
   * delay for its own sake.
   */
  function _detachMenu() {
    const ms = Number.isFinite(opts.exitAnimation) ? opts.exitAnimation : 0;
    if (ms <= 0 || !menu.isConnected || _prefersReducedMotion()) {
      menu.remove();
      return;
    }
    menu.classList.add('pb-popover-menu-closing');
    menu.setAttribute('aria-hidden', 'true');
    menu.style.pointerEvents = 'none';
    setTimeout(() => menu.remove(), ms);
  }

  const handle = close;
  handle.reposition = reposition;

  if (hasRender) {
    const rendered = opts.render({ close, menu, anchorRect: initialAnchorRect });
    if (rendered) menu.appendChild(rendered);
  } else {
    for (const raw of opts.items) {
      if (raw && raw.type === 'separator') {
        const sep = document.createElement('div');
        sep.className = `pb-popover-menu-separator${raw.danger ? ' pb-popover-menu-separator-danger' : ''}`;
        sep.dataset.pbKitPart = 'pb-popover-menu-separator';
        sep.setAttribute('role', 'separator');
        menu.appendChild(sep);
        continue;
      }

      const item = /** @type {PopoverMenuItem} */ (raw);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `pb-popover-menu-item${item.danger ? ' pb-popover-menu-item-danger' : ''}`;
      btn.dataset.pbKitPart = 'pb-popover-menu-item';
      btn.setAttribute('role', item.type === 'radio' ? 'menuitemradio' : 'menuitem');
      if (item.type === 'radio') {
        btn.setAttribute('aria-checked', String(item.checked === true));
        if (item.checked === true) {
          const check = iconEl('check');
          check.classList.add('pb-popover-menu-item-icon');
          check.setAttribute('aria-hidden', 'true');
          btn.appendChild(check);
        }
      }
      btn.tabIndex = item.disabled ? -1 : 0;
      if (item.disabled) {
        btn.setAttribute('aria-disabled', 'true');
        if (item.disabledReason) {
          btn.title = item.disabledReason;
          btn.setAttribute('aria-label', `${item.label} — ${item.disabledReason}`);
        }
      }
      if (item.id) btn.dataset.menuItemId = item.id;

      if (item.icon) {
        const icon = document.createElement('span');
        icon.className = 'pb-popover-menu-item-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = item.icon; // trusted, caller-supplied SVG markup
        btn.appendChild(icon);
      }

      const label = document.createElement('span');
      label.className = 'pb-popover-menu-item-label';
      label.textContent = item.label || '';
      if (item.disabled && item.disabledReason && item.showReason) {
        const reason = document.createElement('span');
        reason.className = 'pb-popover-menu-item-reason';
        reason.textContent = item.disabledReason;
        label.appendChild(reason);
      }
      btn.appendChild(label);

      if (item.shortcut) {
        const shortcut = document.createElement('span');
        shortcut.className = 'pb-popover-menu-item-shortcut';
        shortcut.textContent = item.shortcut;
        btn.appendChild(shortcut);
      }

      if (!item.disabled) {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const handler = item.onSelect;
          close();
          if (typeof handler === 'function') {
            try {
              handler();
            } catch (err) {
              console.error('[popover-menu] item handler threw:', err);
            }
          }
        });
      }
      menu.appendChild(btn);
    }
  }

  focusableItems = _getFocusableItems(menu);
  if (!hasRender && focusableItems.length === 0) return _noopHandle();

  document.body.appendChild(menu);
  if (opts.consumeCanvasDismiss === true) {
    dismissShield = _createDismissShield(close, () => {
      deferShieldRemoval = true;
    });
  }
  reposition();

  _setAriaExpanded(triggerEl, 'true');
  requestAnimationFrame(() => {
    if (!closed) menu.classList.add('pb-popover-menu-open');
  });

  const releaseTrap = trapFocus(menu, { initialFocus: opts.initialFocus });
  focusableItems = _getFocusableItems(menu);

  function reposition() {
    const rect = _resolveAnchorRect(triggerEl, opts);
    if (!rect) return;
    const frame = _visualFrame();
    const gutters = _safeGutters();
    const minLeft = frame.left + gutters.left;
    const maxRight = frame.right - gutters.right;
    const minTop = frame.top + gutters.top;
    const maxBottom = frame.bottom - gutters.bottom;
    const availableW = Math.max(0, maxRight - minLeft);
    const availableH = Math.max(120, maxBottom - minTop);
    const clampedWidth = Math.max(0, Math.min(width, availableW));

    menu.style.maxWidth = `${availableW}px`;
    menu.style.maxHeight = `${availableH}px`;
    if (Number.isFinite(opts.width) || opts.useBaseClass !== false) {
      menu.style.minWidth = `${clampedWidth}px`;
    }

    if (_wantsPhoneSheet(opts)) {
      menu.classList.add('pb-popover-menu-sheet');
      menu.style.left = `${minLeft}px`;
      menu.style.right = 'auto';
      menu.style.width = `${availableW}px`;
      menu.style.minWidth = '0px';
      menu.style.top = 'auto';
      menu.style.bottom = `${_layoutBottomOffset(frame, gutters)}px`;
      return;
    }

    menu.classList.remove('pb-popover-menu-sheet');
    menu.style.right = '';
    menu.style.bottom = '';
    menu.style.width = '';

    const menuW = Math.min(menu.offsetWidth || clampedWidth || width, availableW);
    const menuH = Math.min(menu.offsetHeight || 200, availableH);
    let left;
    if (align === 'left') {
      left = Number(rect.left || 0);
    } else if (align === 'center') {
      left = Number(rect.left || 0) + Number(rect.width || 0) / 2 - menuW / 2;
    } else {
      left = Number(rect.right || 0) - menuW;
    }
    if (left + menuW > maxRight) left = maxRight - menuW;
    if (left < minLeft) left = minLeft;

    const gap = Number.isFinite(opts.gap) ? opts.gap : 4;
    let top = Number(rect.bottom ?? rect.top ?? 0) + gap;
    if (top + menuH > maxBottom) {
      top = Math.max(minTop, Number(rect.top || 0) - menuH - gap);
    }
    if (top < minTop) top = minTop;
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;

    // Contracts §9: the scale-in has its `transform-origin` AT THE TRIGGER, so
    // the menu grows out of the button that opened it instead of out of its own
    // centre. Clamped to the menu box because a menu pushed away from its anchor
    // by the viewport clamp would otherwise scale from a point outside itself,
    // which reads as a slide rather than a grow.
    const anchorX = Number(rect.left || 0) + Number(rect.width || 0) / 2;
    const anchorY = Number(rect.top ?? 0) + Number(rect.height || 0) / 2;
    const originX = Math.max(0, Math.min(menuW, anchorX - left));
    const originY = Math.max(0, Math.min(menuH, anchorY - top));
    menu.style.transformOrigin = `${originX}px ${originY}px`;
  }

  const onTrapEscape = () => {
    if (dismissOpts.escape) close();
  };
  menu.addEventListener('trap-escape', onTrapEscape);

  const onDocClick = (event) => {
    if (!dismissOpts.outsideClick) return;
    if (typeof opts.outsideClickFilter === 'function' && opts.outsideClickFilter(event)) return;
    if (menu.contains(event.target)) return;
    if (triggerEl && (triggerEl === event.target || triggerEl.contains?.(event.target))) return;
    close();
  };

  const onKey = (event) => {
    if (opts.disableArrowNav) return;
    if (isComposing(event)) return;
    const focused = document.activeElement;
    if (isTextEntryControl(focused)) {
      if ([' ', 'Home', 'End', 'Enter'].includes(event.key)) return;
      if (
        (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
        (focused.tagName?.toLowerCase() === 'textarea' || focused.type === 'number')
      )
        return;
    }
    focusableItems = _getFocusableItems(menu);
    if (!focusableItems.length) return;
    const idx = focusableItems.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusableItems[(idx + 1 + focusableItems.length) % focusableItems.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusableItems[(idx - 1 + focusableItems.length) % focusableItems.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusableItems[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      focusableItems[focusableItems.length - 1]?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (document.activeElement && focusableItems.includes(document.activeElement)) {
        event.preventDefault();
        document.activeElement.click();
      }
    }
  };

  let docListenersInstalled = false;
  const docListenerFrame = requestAnimationFrame(() => {
    if (_activeByOwner.get(owner)?.menu !== menu) return;
    if (dismissOpts.outsideClick) {
      document.addEventListener('mousedown', onDocClick, true);
      document.addEventListener('touchstart', onDocClick, true);
      docListenersInstalled = true;
    }
  });
  menu.addEventListener('keydown', onKey);

  const onWinChange = () => reposition();
  window.addEventListener('resize', onWinChange);
  window.addEventListener('scroll', onWinChange, true);
  viewportUnsubscribe = onViewportChange(onWinChange);

  const eventCleanups = [];
  if (dismissOpts.iframeClick) {
    eventCleanups.push(
      eventBus.on('iframe:canvas-clicked', close),
      eventBus.on('iframe:section-clicked', close),
      eventBus.on('section-deselected', close)
    );
  }
  if (dismissOpts.panelTabChange) {
    eventCleanups.push(eventBus.on('panel:tab-changed', close));
  }
  if (dismissOpts.sidebarClose) {
    eventCleanups.push(
      eventBus.on('sidebar:state-changed', ({ open } = {}) => {
        if (open === false) close();
      })
    );
  }

  record = { close, menu, triggerEl, owner };
  _activeByOwner.set(owner, record);
  return handle;
}

/** Close all currently-open popover menus. Returns true if one was closed. */
export function closeActivePopoverMenu() {
  if (_activeByOwner.size === 0) return false;
  for (const record of Array.from(_activeByOwner.values())) {
    record.close();
  }
  return true;
}
